import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Building2 } from "lucide-react";

const FALLBACK = "#1d4ed8";

let metaCache = null; let metaPromise = null;
export function useCompanyMeta() {
  const [meta, setMeta] = useState(metaCache || {});
  useEffect(() => {
    if (metaCache) { setMeta(metaCache); return; }
    metaPromise = metaPromise || api.get("/companies/meta").then(({ data }) => { metaCache = data; return data; }).catch(() => ({}));
    metaPromise.then((d) => setMeta(d));
  }, [metaCache]); // eslint-disable-line react-hooks/exhaustive-deps
  return meta;
}
export function resetCompanyMeta() { metaCache = null; metaPromise = null; }

export function CompanyTag({ customerId, className = "", testid }) {
  const meta = useCompanyMeta();
  const c = customerId ? meta[customerId] : null;
  if (!c) return null;
  const color = c.accent_color || FALLBACK;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-1.5 py-px text-[10px] font-medium leading-4 max-w-full ${className}`} style={{ borderColor: color, color, backgroundColor: `${color}14` }} data-testid={testid} title={c.usdot ? `${c.company} · USDOT ${c.usdot}` : c.company}>
      <span className="h-1.5 w-1.5 rounded-full shrink-0" style={{ backgroundColor: color }} />
      <span className="truncate">{c.company}</span>
    </span>
  );
}

export function activeCompany(user) {
  return (user?.companies || []).find((c) => c.id === user.active_company_id) || null;
}

export function CompanyChip({ user, className = "", testid = "active-company-chip" }) {
  const c = activeCompany(user);
  if (!c || user.role === "admin") return null;
  const color = c.accent_color || FALLBACK;
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-medium max-w-full ${className}`} style={{ borderColor: color, color, backgroundColor: `${color}1a` }} data-testid={testid} title={c.usdot ? `USDOT ${c.usdot}` : undefined}>
      <span className="h-2 w-2 rounded-full shrink-0" style={{ backgroundColor: color }} />
      <span className="truncate">{c.company}</span>
      {c.usdot && <span className="font-mono opacity-70 shrink-0 ml-0.5">· {c.usdot}</span>}
    </span>
  );
}

export function CompanySwitcher({ user }) {
  const { switchCompany } = useAuth();
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);
  const list = user?.companies || [];
  if (list.length < 2) return null;
  const pick = async (id) => {
    if (id === user.active_company_id) return;
    setBusy(true);
    try {
      const u = await switchCompany(id);
      toast.success(`Now working in ${activeCompany(u)?.company || "the selected company"}`);
      nav("/dashboard");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };
  return (
    <div className="px-4 pt-3" data-testid="company-switcher">
      <div className="tag-mono text-slate-500 mb-1 flex items-center gap-1"><Building2 className="h-3 w-3" />Working in</div>
      <Select value={user.active_company_id} onValueChange={pick} disabled={busy}>
        <SelectTrigger className="h-9 bg-slate-900 border-slate-700 text-slate-100 text-sm" data-testid="company-switcher-select"><SelectValue /></SelectTrigger>
        <SelectContent>
          {list.map((c) => (
            <SelectItem key={c.id} value={c.id} data-testid={`company-switcher-option-${c.id}`}>
              <span className="inline-flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: c.accent_color || FALLBACK }} />{c.company}{c.usdot ? <span className="font-mono text-xs text-slate-500">· {c.usdot}</span> : null}</span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
